import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { maskitoNumberOptionsGenerator, maskitoParseNumber } from '@maskito/kit';
import { maskitoTransform } from '@maskito/core';
import { MaskitoDirective } from '@maskito/angular';

@Component({
  selector: 'app-percentage-input',
  templateUrl: './percentage-input.component.html',
  styleUrls: ['./percentage-input.component.scss'],
  standalone: true,
  imports: [MaskitoDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PercentageInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Default
})
export class PercentageInputComponent implements ControlValueAccessor {
  @Input() placeholder: string = '';
  @Input() controlId: string | null = null;
  @Input() error: boolean = false;
  @Output() valueChange = new EventEmitter<number | null>();

  public mask = maskitoNumberOptionsGenerator({
    precision: 2,
    thousandSeparator: '',
    decimalSeparator: '.',
    min: 0
  });

  public value: string = '';
  public disabled: boolean = false;

  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  public writeValue(value: number | null): void {
    this.value = value === null ? '' : maskitoTransform(value?.toFixed(2), this.mask);
  }

  public registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public handleInput(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    this.value = inputValue;

    const numericValue = maskitoParseNumber(inputValue);
    this.valueChange.emit(isNaN(numericValue) ? null : numericValue);
    this.onChange(isNaN(numericValue) ? null : numericValue);
  }

  public handleBlur(): void {
    this.onTouched();
    const numericValue = maskitoParseNumber(this.value);
    if (!isNaN(numericValue)) {
      const formattedValue = numericValue.toFixed(2);
      this.value = maskitoTransform(formattedValue, this.mask);
    } else {
      this.value = '';
    }
  }
}
